import React, { useEffect, useMemo, useState } from "react";

import { Box } from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { editBusinessODPConfig } from "api/business-user-management";
import { IBusinessODPConfigUpdatePayload } from "api/business-user-management/types";
import { ReactComponent as CloseIcon } from "assets/icons/close-icon-in-grey-bg.svg";
import { useParams } from "react-router-dom";
import { formatDayWithOrdinals } from "utils";
import { ZERO } from "utils/constants";

import { Button } from "ui/button";
import { customErrorToast, customSuccessToast } from "ui/custom-toast";
import { Modal } from "ui/modal";
import { defaultModalCss } from "ui/modal/styles";
import { StyledSelect } from "ui/styled-select";
import { IStyledSelectOption } from "ui/styled-select/types";
import { Heading3, Paragraph2 } from "ui/typography";

import {
  ODP_CUT_OFF_VALUE,
  employeeAccessOptions,
  salaryDateOptions,
  whoPaysWithdrawalFeesOptions,
  withdrawalSetUpOptions,
} from "./consts";
import {
  IEditConfigurationFormData,
  IEditConfigurationModalProps,
} from "./types";

const DEFAULT_EDIT_CONFIG_FORM_DATA: IEditConfigurationFormData = {
  salaryDate: null,
  whoPaysWithdrawalFees: null,
  withdrawalSetUp: null,
  employeeAccess: null,
  payrollCutOff: null,
};

export const EditConfigurationModal = (props: IEditConfigurationModalProps) => {
  const {
    isOpen,
    setIsOpen,
    handleToggleEditConfigModal,
    handleGetBusinessOdpConfig,
    odpConfig,
  } = props;
  const { businessId } = useParams();
  const { isPending: isLoading, mutateAsync } = useMutation({
    mutationFn: editBusinessODPConfig,
  });
  const [formData, setFormData] = useState<IEditConfigurationFormData>(
    DEFAULT_EDIT_CONFIG_FORM_DATA
  );

  const handleCloseConfigModal = () => {
    // setFormData(DEFAULT_EDIT_CONFIG_FORM_DATA);
    handleToggleEditConfigModal();
  };

  const handleSetIsOpen = (value: boolean) => {
    setFormData(DEFAULT_EDIT_CONFIG_FORM_DATA);
    setIsOpen(value);
  };

  const handleFormDataUpdate = (name: string, value: IStyledSelectOption) => {
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSaveChanges = async () => {
    if (!businessId) {
      console.error("Business id is absent!");
      return;
    }

    const {
      salaryDate,
      whoPaysWithdrawalFees,
      withdrawalSetUp,
      employeeAccess,
    } = formData;

    const payload: Partial<IBusinessODPConfigUpdatePayload> = {
      businessId,
      paymentFee: whoPaysWithdrawalFees?.value,
      autoWithdrawals: withdrawalSetUp?.value === "true" ? true : false,
      withdrawalPercent: +(employeeAccess?.value || ZERO),
      payrollCutOff: +(salaryDate?.value || ZERO),
    };
    const { error, message } = await mutateAsync(payload);
    if (error) {
      customErrorToast({
        message,
      });
    } else {
      customSuccessToast({ message: "ODP Configuration Updated Successfully" });
      handleGetBusinessOdpConfig(businessId);
      handleCloseConfigModal();
    }
  };

  const isBtnDisabled = useMemo(() => {
    const {
      salaryDate,
      whoPaysWithdrawalFees,
      withdrawalSetUp,
      payrollCutOff,
      employeeAccess,
    } = formData;

    return (
      !salaryDate ||
      !whoPaysWithdrawalFees?.value ||
      !withdrawalSetUp?.value ||
      !payrollCutOff?.value ||
      !employeeAccess?.value
    );
  }, [formData]);

  // React.useEffect(() => console.log("odpcon", odpConfig), [odpConfig]);

  useEffect(() => {
    if (odpConfig) {
      const whoPaysWithdrawalFees = whoPaysWithdrawalFeesOptions.find(
        ({ value }) => value === odpConfig?.paymentFee || ""
      );
      const withdrawalSetUp = withdrawalSetUpOptions.find(
        ({ value }) => value === (odpConfig?.autoWithdrawals?.toString() || "")
      );
      const employeeAccess = employeeAccessOptions.find(
        ({ value }) =>
          String(value) === String(odpConfig?.withdrawalPercent || "")
      );
      const salaryDate = salaryDateOptions.find(
        ({ value }) => value === String(odpConfig?.payrollCutOff || "")
      );
      const payrollCutOffVal = salaryDate?.value
        ? Number(salaryDate.value) - ODP_CUT_OFF_VALUE
        : null;
      setFormData({
        ...formData,
        whoPaysWithdrawalFees: whoPaysWithdrawalFees || null,
        withdrawalSetUp: withdrawalSetUp || null,
        employeeAccess: employeeAccess || null,
        salaryDate: salaryDate || null,
        payrollCutOff: payrollCutOffVal
          ? {
              label: formatDayWithOrdinals(payrollCutOffVal),
              value: String(payrollCutOffVal),
            }
          : null,
      });
    } /* else if (businessId) {
      handleGetBusinessOdpConfig(businessId);
    } */
  }, [odpConfig /* , businessId */]);

  useEffect(() => {
    const payrollCutOffVal = formData.salaryDate?.value
      ? Number(formData.salaryDate.value) - ODP_CUT_OFF_VALUE
      : null;
    setFormData({
      ...formData,
      payrollCutOff: payrollCutOffVal
        ? {
            label: `${formatDayWithOrdinals(payrollCutOffVal)} of every month`,
            value: String(payrollCutOffVal),
          }
        : null,
    });
  }, [formData.salaryDate]);

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={handleSetIsOpen}
      modalCss={{
        ...defaultModalCss,
        width: "501px",
        maxHeight: "677px",
        boxShadow: "0px 0px 7.4px 0px #0000000D",
        padding: "24px 0",
        borderRadius: "12px",
        display: "flex",
        flexDirection: "column",
        gap: "32px",
      }}
      allowOutsideClick={false}
    >
      <Box className="h-[61px] px-6 flex items-center">
        <Box className="h-[58px] border-b border-[#F3F4F6] flex w-full justify-between">
          <Box className="flex flex-col gap-[10px]">
            <Heading3
              lineHeight="27.32px"
              textColor="#1A202C"
              fontWeight="700"
            >
              Edit Configuration
            </Heading3>
            <Paragraph2
              lineHeight="19.2px"
              textColor="#4A5564"
            >
              Change the configuration for this business
            </Paragraph2>
          </Box>
          <CloseIcon
            className={`${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}
            onClick={!isLoading ? handleCloseConfigModal : undefined}
          />
        </Box>
      </Box>
      <Box className="px-6 flex flex-col gap-6">
        <StyledSelect
          label="Salary Date"
          options={salaryDateOptions}
          name="salaryDate"
          value={formData.salaryDate}
          handleOnChange={(value) => {
            const selectedValue = value as IStyledSelectOption;
            handleFormDataUpdate("salaryDate", selectedValue);
          }}
          menuPlacement="auto"
        />
        <StyledSelect
          label="Who pays withdrawal fee?"
          options={whoPaysWithdrawalFeesOptions}
          name="whoPaysWithdrawalFees"
          value={formData.whoPaysWithdrawalFees}
          handleOnChange={(value) => {
            const selectedValue = value as IStyledSelectOption;
            handleFormDataUpdate("whoPaysWithdrawalFees", selectedValue);
          }}
          menuPlacement="auto"
        />
        <StyledSelect
          label="Withdrawal Setup"
          options={withdrawalSetUpOptions}
          name="withdrawalSetUp"
          value={formData.withdrawalSetUp}
          handleOnChange={(value) => {
            const selectedValue = value as IStyledSelectOption;
            handleFormDataUpdate("withdrawalSetUp", selectedValue);
          }}
          menuPlacement="auto"
        />
        <StyledSelect
          label="Employee Access (%)"
          options={employeeAccessOptions}
          name="employeeAccess"
          value={formData.employeeAccess}
          handleOnChange={(value) => {
            const selectedValue = value as IStyledSelectOption;
            handleFormDataUpdate("employeeAccess", selectedValue);
          }}
          menuPlacement="auto"
        />
        <StyledSelect
          label="Cut-Off Date"
          options={[]}
          name="payrollCutOff"
          value={formData.payrollCutOff}
          handleOnChange={(value) => {
            const selectedValue = value as IStyledSelectOption;
            handleFormDataUpdate("payrollCutOff", selectedValue);
          }}
          menuPlacement="auto"
          isDisabled
        />
      </Box>
      <Box className="flex items-center justify-end h-12 gap-3 px-6">
        <Button
          variant="outline"
          textColor="#344054"
          border="1px solid #D0D5DD"
          className={`w-[90px] h-12 ${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}
          onClick={!isLoading ? handleCloseConfigModal : undefined}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          variant="solid"
          textColor="#ffffff"
          border="1px solid #00535C"
          className={`w-[157px] h-12 text-[16px] leading-5 font-semibold ${isLoading || isBtnDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}
          onClick={!isBtnDisabled && !isLoading ? handleSaveChanges : undefined}
          disabled={isLoading || isBtnDisabled}
          loading={isLoading}
          loadingText="Saving..."
        >
          Save Changes
        </Button>
      </Box>
    </Modal>
  );
};
